import { Box, Link as MuiLink, Typography } from '@mui/material'
import * as React from 'react'
import { camelCaseFormatter } from './formatters/camelCase'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined'
import { useTranslation } from 'react-i18next'
import { RenderTooltip } from './RenderTooltip'
import './renderJSON.scss'

export const ExpandableProperty = ({ expanded, title, tooltip, children, quote }) => {
  const [isOpen, setIsOpen] = React.useState(!!expanded)
  const { t } = useTranslation(['common'])

  return (
    <React.Fragment>
      {title && (
        <Box className="rjson-wrapper" onClick={() => setIsOpen(!isOpen)} sx={{ cursor: 'pointer' }}>
          <Box className="recursive-pair">
            <Box className="recursive-pair">
              <Typography variant="text2">{title}</Typography>
              {tooltip && <RenderTooltip title={tooltip} />}
              <Typography variant="text2">:</Typography>
            </Box>
            {isOpen ? (
              <Box className="opening">
                <MuiLink>{t('common:seeMore')}</MuiLink>
                <KeyboardArrowDownOutlinedIcon color="secondary" className="arrow" />
              </Box>
            ) : (
              <Box className="opening">
                <MuiLink>{t('common:seeMore')}</MuiLink>
                <KeyboardArrowRightOutlinedIcon color="secondary" className="arrow" />
              </Box>
            )}
          </Box>
          {quote && (
            <Typography
              variant="caption"
              sx={{ fontStyle: 'italic', display: 'block', pl: 2, color: 'text.secondary' }}
            >
              {quote}
            </Typography>
          )}
        </Box>
      )}
      {isOpen ? <Box>{children}</Box> : null}
      {React.Children.count(children) === 0 && isOpen ? '---' : null}
    </React.Fragment>
  )
}

const isWrapperObject = (obj) => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'value' in obj &&
    Object.keys(obj).length <= 2 &&
    typeof obj.value !== 'object'
  )
}

const isUrl = (value) => {
  return typeof value === 'string' && /^https?:\/\/.+/i.test(value)
}

const RecursiveProperty = ({ property, rootProperty, propertyName }) => {
  const { t } = useTranslation(['common', 'messages'])

  const isWrapped =
    typeof property === 'object' && property !== null && Object.prototype.hasOwnProperty.call(property, 'value')

  const value = isWrapped ? property.value : property
  const tooltipKey = isWrapped ? property.tooltip : undefined
  const quoteKey = isWrapped ? property.quote : undefined

  const shouldRenderAsPrimitive = typeof value !== 'object' || value === null || isWrapperObject(property)

  return (
    <Box className="recursive-wrapper" style={{ marginLeft: rootProperty ? 0 : 25 }}>
      {property !== undefined &&
        propertyName !== 'resultados_sugeridos' &&
        propertyName !== 'site_receipt' &&
        (shouldRenderAsPrimitive ? (
          <Box display="flex" flexDirection="column">
            <Box className="recursive-pair">
              {propertyName && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Typography variant="text2">{camelCaseFormatter(propertyName)}</Typography>
                  {tooltipKey && <RenderTooltip title={t(`messages:${tooltipKey}.tooltip`, tooltipKey)} />}
                  <Typography variant="text2">:</Typography>
                </Box>
              )}
              {isUrl(value) ? (
                <MuiLink href={value} target="_blank" rel="noopener noreferrer">
                  {value}
                </MuiLink>
              ) : (
                <p>{value === '' || value === null || value === undefined ? '---' : value.toString()}</p>
              )}
            </Box>
            {quoteKey && (
              <Typography
                variant="caption"
                sx={{ fontStyle: 'italic', display: 'block', pl: 2, color: 'text.secondary' }}
              >
                {t(`messages:${quoteKey}.quote`, quoteKey)}
              </Typography>
            )}
          </Box>
        ) : (
          <ExpandableProperty
            title={propertyName && camelCaseFormatter(propertyName)}
            tooltip={tooltipKey ? t(`messages:${tooltipKey}.tooltip`, tooltipKey) : undefined}
            quote={quoteKey ? t(`messages:${quoteKey}.quote`, quoteKey) : undefined}
            expanded={!!rootProperty}
          >
            {value &&
              Object.values(value).map((p, index) => (
                <RecursiveProperty key={index} property={p} propertyName={Object.getOwnPropertyNames(value)[index]} />
              ))}
          </ExpandableProperty>
        ))}
    </Box>
  )
}

export default RecursiveProperty
